/**
 * Banco de pruebas de calibración.
 *
 * Se escoge UN vehículo, se trae su recorrido, y a partir de ahí todo pasa en
 * el navegador: cada vez que se mueve una perilla se vuelve a correr el motor
 * —el de verdad, `inspectDriving`, que comparte código con
 * `analyzeDrivingHistoryV5` y tiene una prueba que exige que cuenten igual— y
 * se ve al instante qué tramos disparan y cuáles no.
 *
 * Por qué así y no calculando en el servidor: calibrar es mover un número y
 * mirar. Si cada movimiento costara una petición, nadie movería el número más
 * de tres veces, y la calibración volvería a ser una corazonada con mejor letra.
 *
 * Y cuando el número queda bien hay que decir **para quién**. Durante un tiempo
 * esta pantalla guardaba siempre para la cadencia, con el argumento de que dos
 * equipos que reportan cada 10 s miden la misma magnitud. Miden la misma
 * magnitud, sí, pero no tienen la misma distribución: los siete vehículos de
 * 10 s de la flota se separan por un factor 3,5 en aceleración y 4,7 en giro
 * según si andan en reparto urbano a 20 km/h o en carretera a 43. Con los
 * umbrales de carretera los urbanos marcan una de cada cinco maniobras; con los
 * urbanos, los de carretera no marcan nada.
 *
 * Así que el alcance se pregunta y no se supone, y se pregunta sin valor por
 * omisión: es la decisión más cara de esta pantalla.
 */

import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { inspectDriving, measureCandidateValues } from "@core/pipeline/driving-analysis-v5.ts";
import type { GuardiaHistoryRecord } from "@core/contracts/telemetry.ts";
import { familySamples, proposeThresholdsAtPercentile } from "@core/calibration/calibration-lab.ts";
import { checkThresholdsAgainstSample } from "@core/calibration/threshold-guard.ts";
import { checkFleetCoverage, MINIMUM_FLEET_DISTANCE_KM } from "@core/calibration/fleet-coverage.ts";
import { shortDigest } from "@core/calibration/sample-identity.ts";
import { computeAnalysisSignature, computeProfileDigest } from "@core/pipeline/signature.ts";
import {
  MANUAL_OFICIAL_V3,
  manualYawRateAt,
  applyThresholds, PROFILE_FAMILY,
  type AnalysisProfile,
} from "@core/pipeline/profiles.ts";
import {
  API_ERROR_CODES,
  type CalibrationVehicleResponse,
  type SignedFleetEvidence,
  type TunableThresholds,
  type Vehicle,
} from "@core/contracts/api.ts";
import {
  ApiError,
  deactivateCalibration,
  fetchCalibrationProfiles,
  requestCalibrationVehicle,
  requestFleetCheck,
  saveCalibration,
} from "@web/lib/api-client.ts";
import { toBatchPage, useBatchWalk } from "@web/lib/batch-walk.ts";
import { COMPONENT_LABELS } from "@core/report/drivescore-v1.ts";
import { Card, Notice, Stat } from "@web/components/primitives.tsx";
import { SeverityControls, ThresholdControls } from "./threshold-controls.tsx";
import { SegmentTable } from "./segment-table.tsx";
import { DistributionStrip } from "./distribution-strip.tsx";

const REGIME_LABEL: Record<string, string> = {
  HIGH: "alta frecuencia (menos de 3 s)",
  MEDIUM: "media (3 a 7 s)",
  LOW: "baja (7 s o más)",
};

const num = (value: number | null, digits = 2) =>
  value === null ? "—" : value.toLocaleString("es-CO", { maximumFractionDigits: digits });

export function CalibrationPanel({
  period,
  vehicles,
}: {
  period: { fromIso: string; toIso: string } | null;
  vehicles: Vehicle[];
}) {
  const queryClient = useQueryClient();
  const [deviceId, setDeviceId] = useState("");
  const [loaded, setLoaded] = useState<CalibrationVehicleResponse | null>(null);
  /*
   * El período con el que se CARGÓ la muestra, no el que esté seleccionado.
   *
   * Hasta el 07/10/2026 el motor de esta pantalla y el guardado leían el
   * período seleccionado en cada momento. Cambiarlo sin volver a cargar dejaba
   * los registros viejos recortados al período nuevo, y el guardado le pedía al
   * servidor que verificara otra muestra. Todo lo que es «esta muestra» sale de
   * aquí; el período seleccionado solo sirve para la próxima carga y para la
   * verificación de flota, que tiene su propio período.
   */
  const [loadedPeriod, setLoadedPeriod] = useState<{ fromIso: string; toIso: string } | null>(null);
  const [thresholds, setThresholds] = useState<TunableThresholds | null>(null);
  const [notes, setNotes] = useState("");
  // `null` = todavía no se escogió. Sin valor por omisión a propósito: guardar
  // para la cadencia entera cuando se quería guardar para un vehículo cambia los
  // umbrales de toda la flota, y al contrario deja la flota sin calibrar creyendo
  // que se calibró.
  const [scope, setScope] = useState<"VEHICULO" | "CADENCIA" | null>(null);

  const profiles = useQuery({
    queryKey: ["calibration-profiles"],
    queryFn: fetchCalibrationProfiles,
  });

  /*
   * Verificación de flota de un umbral común (ADR-009).
   *
   * Con alcance de cadencia, guardar ya no se decide contra el p99 de ESTE
   * vehículo sino contra la flota de su cadencia: el servidor mide cada vehículo
   * con los umbrales de la pantalla y firma lo que midió, y al guardar se le
   * devuelve esa evidencia. Lo que se verificó queda fijado aquí: si después se
   * mueve una perilla, la verificación deja de valer y hay que repetirla.
   */
  const fleetTarget = useRef<{ regime: "HIGH" | "MEDIUM" | "LOW"; thresholds: TunableThresholds } | null>(null);
  const [verifiedKey, setVerifiedKey] = useState<string | null>(null);
  const fetchFleetPage = useCallback(
    async (input: { cursor: string | null; fromIso: string; toIso: string }) => {
      const target = fleetTarget.current;
      if (!target) throw new ApiError("INVALID_REQUEST", "No hay umbrales que verificar.", 400);
      const response = await requestFleetCheck({ ...input, regime: target.regime, thresholds: target.thresholds });
      return toBatchPage<SignedFleetEvidence>(response, response.items);
    },
    [],
  );
  const fleetWalk = useBatchWalk<SignedFleetEvidence>(fetchFleetPage);

  const load = useMutation({
    mutationFn: (requested: { fromIso: string; toIso: string } | null) => {
      if (!requested || !deviceId) throw new ApiError("INVALID_REQUEST", "Falta vehículo o período.", 400);
      return requestCalibrationVehicle({ deviceId, ...requested });
    },
    onSuccess: (data, requested) => {
      setLoaded(data);
      setLoadedPeriod(requested);
      setThresholds(data.thresholds);
    },
  });

  const save = useMutation({
    mutationFn: () => {
      if (!loaded || !thresholds) throw new ApiError("INVALID_REQUEST", "No hay nada que guardar.", 400);
      if (!scope) {
        throw new ApiError("INVALID_REQUEST", "Escoge primero si esto aplica a este vehículo o a toda la cadencia.", 400);
      }
      if (scope === "CADENCIA" && !fleetReady) {
        throw new ApiError(
          "INVALID_REQUEST",
          "Primero verifica estos umbrales contra la flota de la cadencia, y que la verificación los respalde.",
          400,
        );
      }
      return saveCalibration({
        regime: loaded.profileSelection.regime,
        thresholds,
        scope,
        calibratedFromDevice: loaded.vehicle.deviceId,
        calibratedFromPlate: loaded.vehicle.plate,
        // El período y la huella de la muestra cargada: con ellos el servidor
        // vuelve a medir la misma muestra y dice si lo es.
        calibratedFromIso: loadedPeriod?.fromIso ?? null,
        calibratedToIso: loadedPeriod?.toIso ?? null,
        sampleInputDigest: loaded.sampleIdentity?.inputDigest ?? null,
        sampleSegments: inspection?.eligibleSegments ?? null,
        notes: notes.trim() ? notes.trim() : null,
        ...(scope === "CADENCIA" ? { fleetEvidence: fleetWalk.items } : {}),
      });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["calibration-profiles"] }),
  });

  /*
   * Volver a cargar la MISMA muestra (el período de la carga, no el del
   * selector) sin tocar los controles: lo que se pretendía guardar se vuelve a
   * mirar sobre lo que devuelve hoy la telemetría. Lo ofrece la pantalla cuando
   * el servidor dice que la muestra cambió (D3, ADR-013).
   */
  const reloadSample = useMutation({
    mutationFn: () => {
      if (!loadedPeriod || !loaded) throw new ApiError("INVALID_REQUEST", "No hay una muestra cargada.", 400);
      return requestCalibrationVehicle({ deviceId: loaded.vehicle.deviceId, ...loadedPeriod });
    },
    onSuccess: (data) => {
      setLoaded(data);
      save.reset();
    },
  });

  /** El servidor dijo que la muestra cambió o no la pudo comprobar (D3), o guardó sobre otra muestra. */
  const sampleChanged =
    (save.isError && save.error instanceof ApiError && save.error.code === API_ERROR_CODES.SAMPLE_CHANGED) ||
    (save.isSuccess && save.data.verifiedSample?.matchesLab === false);

  // Desactiva lo que esté aplicándose AHORA a este vehículo, que es lo que el
  // botón promete. Si lo que manda es su perfil propio, quitar el de la cadencia
  // no cambiaría nada de lo que se está viendo en pantalla.
  const revert = useMutation({
    mutationFn: () => {
      if (!loaded) throw new ApiError("INVALID_REQUEST", "No hay régimen.", 400);
      return deactivateCalibration(
        loaded.profileSelection.regime,
        loaded.profileSelection.scope === "VEHICULO" ? loaded.profileSelection.scopeDevice : null,
      );
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["calibration-profiles"] }),
  });

  // El recálculo se difiere: mover un deslizador no puede trabar el teclado.
  const deferred = useDeferredValue(thresholds);
  const recomputing = thresholds !== deferred;

  /**
   * Las dos corridas del motor, con el MISMO perfil y el MISMO período.
   *
   * `inspectDriving` explica fila por fila —para eso existe— y por eso sus
   * pares NO aplican las exclusiones del detector de giro: ni la media vuelta
   * de 180° exactos ni el recorrido mínimo para que el rumbo signifique algo.
   * Sirve para decir «este par no disparó porque…», y no sirve para hacer la
   * muestra.
   *
   * `measureCandidateValues` sí las aplica, y es la función con la que el
   * servidor vuelve a medir al guardar. Todo lo que sea muestra —la tira de
   * distribución, el percentil que propone el botón y el veredicto de la
   * guarda— sale de ahí. Mientras salía de los pares, el Laboratorio proponía
   * un p99 de giro de 18 °/s (los 180° de ruido de rumbo divididos por la
   * cadencia de 10 s), la guarda de pantalla lo aprobaba, y el servidor
   * respondía 400 contra su propio p99 de 1,98. Y lo más caro no era el
   * rechazo: con ese 18 en pantalla se tomaron decisiones de calibración el
   * 29/09.
   *
   * Cuesta un segundo recorrido de los registros en el navegador por cada
   * movimiento de perilla. Se paga: la alternativa era volver a escribir las
   * exclusiones aquí, que es la copia que produjo el defecto.
   */
  const engine = useMemo(() => {
    if (!loaded || !deferred || !loadedPeriod) return null;
    const base = loaded.profileSelection;
    const input = {
      records: loaded.records as unknown as GuardiaHistoryRecord[],
      received: loaded.received,
      requestedFrom: loadedPeriod.fromIso,
      requestedTo: loadedPeriod.toIso,
    };
    const profile = profileFromThresholds(loaded, deferred, base.profileId, base.regime);
    return {
      profile,
      inspection: inspectDriving(input, profile),
      measurements: measureCandidateValues(input, profile),
    };
  }, [loaded, deferred, loadedPeriod]);

  const inspection = engine?.inspection ?? null;

  /*
   * Dos huellas, que no son lo mismo:
   *
   * - la de los controles: el perfil completo que está corriendo;
   * - la firma de esta corrida (`computeAnalysisSignature`): equipo, período,
   *   zona, huella de los registros, huella del perfil, motor y pipeline. Es la
   *   identidad completa de la ejecución.
   *
   * La huella de los registros sola no basta: con la misma muestra, mover una
   * perilla da otra corrida. Dos pantallas con la misma firma calcularon lo
   * mismo.
   */
  const [controlsDigest, setControlsDigest] = useState<string | null>(null);
  const [runSignature, setRunSignature] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    if (!engine) {
      setControlsDigest(null);
      setRunSignature(null);
      return;
    }
    const identity = loaded?.sampleIdentity;
    void computeProfileDigest(engine.profile).then(async (profileDigest) => {
      const signature = identity
        ? await computeAnalysisSignature({
            deviceId: identity.deviceId,
            requestedFrom: identity.fromIso,
            requestedTo: identity.toIso,
            timezone: identity.timezone,
            inputDigest: identity.inputDigest,
            profileDigest,
          })
        : null;
      if (current) {
        setControlsDigest(profileDigest);
        setRunSignature(signature);
      }
    });
    return () => {
      current = false;
    };
  }, [engine, loaded]);

  const periodChanged =
    loadedPeriod !== null &&
    period !== null &&
    (period.fromIso !== loadedPeriod.fromIso || period.toIso !== loadedPeriod.toIso);
  // La misma definición de muestra por familia que usa la guarda del servidor.
  const samples = engine ? familySamples(engine.measurements) : null;

  const update = useCallback((key: keyof TunableThresholds, value: number | [number, number, number]) => {
    setThresholds((previous) => (previous ? { ...previous, [key]: value } : previous));
  }, []);

  /**
   * «Poner los umbrales en el percentil p de este vehículo.»
   *
   * La cuenta vive en el núcleo (`proposeThresholdsAtPercentile`) y sobre las
   * mediciones del motor, no sobre los pares de la inspección: lo que este
   * botón propone tiene que ser algo que el servidor pueda aceptar, y el
   * servidor mide con `measureCandidateValues`. Aquí quedó solo el cable.
   */
  const suggest = useCallback(
    (q: number) => {
      if (!engine || !thresholds) return;
      setThresholds(
        proposeThresholdsAtPercentile({
          measurements: engine.measurements,
          percentileUsed: q,
          current: thresholds,
        }),
      );
    },
    [engine, thresholds],
  );

  /**
   * El mismo veredicto y las mismas advertencias que va a calcular el servidor,
   * anticipados antes de apretar el botón.
   *
   * Es la MISMA función —`checkThresholdsAgainstSample`— con el mismo perfil y
   * la misma muestra: el servidor la vuelve a medir desde la telemetría porque
   * una validación que se calcula solo en el navegador se puede saltar, pero la
   * regla es una sola. Si aquí se armara otra muestra, la pantalla diría que se puede
   * guardar y el servidor lo negaría, o al revés — que es justo lo que pasaba
   * mientras la de aquí salía de `inspection.pairs`.
   */
  const guard = useMemo(() => {
    if (!engine) return null;
    return checkThresholdsAgainstSample({
      profile: engine.profile,
      measurements: engine.measurements,
      // Los episodios que el motor cuenta en esta misma pantalla, con el mismo
      // perfil y la misma muestra: los mismos que el servidor le pasa a la
      // guarda. Sin ellos el aviso no puede decir cuántos episodios saldrían,
      // y «ninguna medición llega al umbral» no es «ningún episodio».
      episodes: {
        aceleraciones: engine.inspection.counts.acceleration,
        frenadas: engine.inspection.counts.braking,
        giros: engine.inspection.counts.turn,
        velocidadAlta: engine.inspection.counts.elevatedSpeed,
      },
    });
  }, [engine]);

  /** Lo que la flota dice de los umbrales verificados. La MISMA función que decide el servidor. */
  const fleetCheck = useMemo(() => {
    const target = fleetTarget.current;
    if (!fleetWalk.complete || !target) return null;
    return checkFleetCoverage(
      target.regime,
      fleetWalk.items.map((item) => ({
        deviceId: item.deviceId,
        regime: item.vehicleRegime,
        distanceKm: item.distanceKm,
        firing: item.firing,
      })),
    );
  }, [fleetWalk.complete, fleetWalk.items]);

  const currentKey =
    loaded && thresholds && period
      ? JSON.stringify([loaded.profileSelection.regime, thresholds, period.fromIso, period.toIso])
      : null;
  // La verificación vale para los umbrales, la cadencia y el período con que se
  // hizo. Mover una perilla después la invalida.
  const fleetFresh = verifiedKey !== null && verifiedKey === currentKey;
  const fleetReady = fleetFresh && fleetWalk.complete && fleetCheck?.ok === true;

  const startFleetCheck = () => {
    if (!loaded || !thresholds || !period || !currentKey) return;
    fleetTarget.current = { regime: loaded.profileSelection.regime, thresholds };
    setVerifiedKey(currentKey);
    void fleetWalk.start(period);
  };

  const activeProfilesHere = profiles.data?.profiles.filter((profile) => profile.active) ?? [];
  const activeForRegime = activeProfilesHere.find(
    (profile) => profile.scopeDevice === null && profile.regime === loaded?.profileSelection.regime,
  );
  // Con el régimen, no solo con el equipo: un mismo vehículo puede tener perfil
  // propio activo en dos cadencias (el índice único de la tabla lo permite, y
  // pasa cuando se le cambia la configuración de reporte). Buscando solo por
  // equipo, el botón de «quitarle el perfil propio» aparecía apoyándose en la
  // fila de OTRA cadencia, que no es la que se está viendo en pantalla.
  const activeForVehicle = activeProfilesHere.find(
    (profile) =>
      profile.scopeDevice !== null &&
      profile.scopeDevice === loaded?.vehicle.deviceId &&
      profile.regime === loaded?.profileSelection.regime,
  );

  return (
    <div className="space-y-4">
      <Card
        title="Banco de pruebas"
        hint="Escoge un vehículo, mira tramo por tramo qué se midió, mueve los umbrales y ve al instante qué se dispara. Al guardar se pregunta si lo que quedó aplica solo a este vehículo o a todos los equipos que reporten a esa misma cadencia."
      >
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            Vehículo
            <select
              className="min-w-56 rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              value={deviceId}
              onChange={(event) => setDeviceId(event.target.value)}
            >
              <option value="">Selecciona…</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.deviceId} value={vehicle.deviceId}>
                  {vehicle.plate ?? vehicle.name ?? vehicle.deviceId}
                  {vehicle.group ? ` — ${vehicle.group}` : ""}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            disabled={!period || !deviceId || load.isPending}
            onClick={() => load.mutate(period)}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {load.isPending ? "Trayendo el recorrido…" : "Cargar recorrido"}
          </button>
        </div>

        {load.isError ? (
          <div className="mt-3">
            <Notice title="No se pudo traer el recorrido">
              {load.error instanceof ApiError ? load.error.message : "Error inesperado."}
            </Notice>
          </div>
        ) : null}
      </Card>

      {loaded && inspection && thresholds ? (
        <>
          <Card
            title={`${loaded.vehicle.plate ?? loaded.vehicle.deviceId} · cadencia ${REGIME_LABEL[loaded.profileSelection.regime] ?? loaded.profileSelection.regime}`}
            hint={`En marcha, el equipo reporta un punto cada ${num(loaded.sampling.medianIntervalSeconds, 1)} s. Umbrales de arranque: ${
              loaded.profileSelection.scope === "VEHICULO"
                ? `los que este vehículo ya tiene para sí (${loaded.profileSelection.profileId}, versión ${loaded.profileSelection.storedVersion})`
                : loaded.profileSelection.scope === "CADENCIA"
                  ? `los calibrados de la cadencia (${loaded.profileSelection.profileId}, versión ${loaded.profileSelection.storedVersion})`
                  : `los provisionales del código (${loaded.profileSelection.profileId})`
            }.`}
          >
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat label="Tramos elegibles" value={inspection.eligibleSegments.toLocaleString("es-CO")} />
              <Stat label="Distancia" value={num(inspection.eligibleDistanceKm, 1)} unit="km" />
              <Stat label="Puntos recibidos" value={loaded.received.toLocaleString("es-CO")} />
              <Stat
                label="Instantes duplicados"
                value={loaded.sampling.duplicateInstants.toLocaleString("es-CO")}
                caveat={
                  loaded.sampling.duplicateInstantPercent
                    ? `${num(loaded.sampling.duplicateInstantPercent, 1)} % de los intervalos`
                    : undefined
                }
              />
            </dl>
            {loaded.sampleIdentity ? (
              <dl className="mt-4 grid grid-cols-1 gap-4 border-t border-zinc-100 pt-3 sm:grid-cols-3 dark:border-zinc-800">
                <Stat
                  label="Muestra (huella de los registros)"
                  value={shortDigest(loaded.sampleIdentity.inputDigest)}
                  caveat={
                    `${loaded.sampleIdentity.records.toLocaleString("es-CO")} registros · ` +
                    `del ${loaded.sampleIdentity.fromIso} al ${loaded.sampleIdentity.toIso} ` +
                    `(${loaded.sampleIdentity.timezone}) · motor ${loaded.sampleIdentity.engineVersion}`
                  }
                />
                <Stat
                  label="Controles (huella del perfil)"
                  value={controlsDigest ? shortDigest(controlsDigest) : "…"}
                  caveat="Cambia con cualquier perilla."
                />
                <Stat
                  label="Firma de esta corrida"
                  value={runSignature ? shortDigest(runSignature) : "…"}
                  caveat="Registros, período, zona, perfil, motor y pipeline. Misma firma, mismas cifras."
                />
              </dl>
            ) : null}
            {periodChanged ? (
              <Notice tone="warning" title="El período seleccionado ya no es el de este recorrido">
                Lo que ves y lo que se guarda es la muestra cargada, del {loadedPeriod?.fromIso} al{" "}
                {loadedPeriod?.toIso}. Vuelve a cargar el recorrido para trabajar con el período nuevo.
              </Notice>
            ) : null}
          </Card>

          <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
            <div className="space-y-4">
              <Card title="Umbrales de detección" hint="Mueve y mira. El recálculo es inmediato.">
                <ThresholdControls
                  thresholds={thresholds}
                  intervalSeconds={loaded.sampling.medianIntervalSeconds}
                  onChange={(key, value) => update(key, value)}
                />
                <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-200 pt-3 dark:border-zinc-800">
                  <span className="w-full text-xs text-zinc-500 dark:text-zinc-400">
                    Poner los umbrales en un percentil de este vehículo:
                  </span>
                  {[90, 95, 97.5, 99].map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => suggest(q)}
                      className="rounded border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
                    >
                      p{q}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setThresholds(loaded.thresholds)}
                    className="rounded border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
                  >
                    Volver a los actuales
                  </button>
                </div>

                <div className="mt-3 border-t border-zinc-200 pt-3 dark:border-zinc-800">
                  <button
                    type="button"
                    onClick={() => setThresholds(manualPreset(thresholds))}
                    className="rounded-md border border-brand-600 px-2.5 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950/40"
                  >
                    Cargar manual oficial GuardIA v3
                  </button>
                  <p className="mt-1.5 text-xs leading-snug text-zinc-500 dark:text-zinc-400">
                    ±2,5 m/s² simétricos · giro por aceleración lateral de 3,4 m/s² con mínimo
                    de 40 km/h y 20° · velocidad alta desde 80 km/h sostenida 5 s, y la racha
                    no se corta hasta bajar de 76. Los tres criterios nuevos —aceleración
                    lateral, ventana sostenida e histéresis de salida— solo se activan
                    cargando este preset: en el código nacen apagados.
                    {loaded.profileSelection.regime === "LOW" ? (
                      <span className="text-amber-700 dark:text-amber-500">
                        {" "}
                        Ojo: el manual exige que entre dos puntos pasen 5 s o menos, y este equipo
                        reporta cada {num(loaded.sampling.medianIntervalSeconds, 1)} s en marcha. Con estos
                        umbrales las aceleraciones y frenadas no se van a disparar nunca — vas a ver
                        el histograma entero por debajo de la línea.
                      </span>
                    ) : null}
                  </p>
                </div>
              </Card>

              <Card title="Bandas de severidad">
                <SeverityControls thresholds={thresholds} onChange={(key, value) => update(key, value)} />
              </Card>
            </div>

            <div className="space-y-4">
              <Card
                title="Con estos umbrales"
                hint={recomputing ? "Recalculando…" : "Es lo que produciría el informe ahora mismo."}
              >
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                  <Stat label="Aceleraciones" value={inspection.counts.acceleration} />
                  <Stat label="Frenadas" value={inspection.counts.braking} />
                  <Stat label="Giros" value={inspection.counts.turn} />
                  <Stat label="Velocidad alta" value={inspection.counts.elevatedSpeed} />
                  <Stat
                    label="Total /100 km"
                    value={num(inspection.ratesPer100Km.total, 1)}
                    caveat={
                      (inspection.ratesPer100Km.total ?? 0) > 60
                        ? "más de un episodio cada 1,7 km"
                        : undefined
                    }
                  />
                </dl>

                {/* Las tres tiras salen de la muestra del motor, la misma que
                    veta el servidor. Dibujarlas desde `inspection.pairs` ponía
                    en la del giro una cola de 18 °/s que no era una medición:
                    era el cambio de rumbo de 180° exactos que el detector
                    descarta, dividido por la cadencia. */}
                <div className="mt-5 space-y-4 border-t border-zinc-200 pt-4 dark:border-zinc-800">
                  <DistributionStrip
                    label="Aceleración"
                    unit="m/s²"
                    values={samples?.acceleration ?? []}
                    threshold={thresholds.accelerationThresholdMps2}
                  />
                  <DistributionStrip
                    label="Frenada"
                    unit="m/s²"
                    values={samples?.braking ?? []}
                    threshold={Math.abs(thresholds.brakingThresholdMps2)}
                  />
                  <DistributionStrip
                    label="Giro"
                    unit="°/s"
                    values={samples?.yaw ?? []}
                    threshold={thresholds.minimumYawRateDegreesPerSecond}
                  />
                </div>
              </Card>

              <Card
                title="Tramo por tramo"
                hint="La última columna dice por qué NO disparó. Es lo que permite mover la perilla sabiendo qué se mueve."
              >
                <SegmentTable
                  pairs={inspection.pairs}
                  segments={inspection.segments}
                  accelerationThreshold={thresholds.accelerationThresholdMps2}
                  brakingThreshold={thresholds.brakingThresholdMps2}
                  yawThreshold={thresholds.minimumYawRateDegreesPerSecond}
                />
              </Card>
            </div>
          </div>

          <Card
            title="Guardar la calibración"
            hint="Primero: ¿para quién? No sobreescribe nada: crea una versión nueva, y la huella del perfil va en la firma de cada informe, así que los informes viejos se siguen pudiendo reconstruir."
          >
            <fieldset className="mb-3 space-y-2">
              <legend className="mb-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-400">
                Alcance de estos umbrales
              </legend>
              {(
                [
                  {
                    value: "VEHICULO" as const,
                    title: `Solo para ${loaded.vehicle.plate ?? loaded.vehicle.deviceId}`,
                    detail:
                      "Este vehículo se mide contra su propia distribución. Los demás de la misma " +
                      "cadencia siguen con lo que tengan hoy.",
                  },
                  {
                    value: "CADENCIA" as const,
                    title: `Para toda la cadencia ${REGIME_LABEL[loaded.profileSelection.regime] ?? ""}`,
                    detail:
                      "Queda activo para todos los equipos que reporten a esta cadencia. Antes de guardar se " +
                      "verifica contra la flota de esa cadencia: cada familia tiene que poder dispararse en al " +
                      `menos la mitad de sus vehículos con ${MINIMUM_FLEET_DISTANCE_KM} km o más.`,
                  },
                ]
              ).map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer gap-2 rounded-md border border-zinc-200 p-2 text-sm has-checked:border-brand-600 dark:border-zinc-800"
                >
                  <input
                    type="radio"
                    name="calibration-scope"
                    className="mt-1"
                    checked={scope === option.value}
                    onChange={() => setScope(option.value)}
                  />
                  <span>
                    <span className="font-medium text-zinc-800 dark:text-zinc-200">{option.title}</span>
                    <span className="block text-xs leading-snug text-zinc-500 dark:text-zinc-400">
                      {option.detail}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            {scope === "CADENCIA" ? (
              <div className="mb-3 space-y-2 rounded-md border border-zinc-200 p-2 text-sm dark:border-zinc-800">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={fleetWalk.running || !period}
                    onClick={startFleetCheck}
                    className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm dark:border-zinc-700"
                  >
                    {fleetWalk.running ? "Verificando…" : "Verificar contra la flota"}
                  </button>
                  {fleetWalk.running ? (
                    <>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">
                        {fleetWalk.processed} de {fleetWalk.total || "…"} vehículos
                      </span>
                      <button type="button" onClick={fleetWalk.stop} className="text-xs underline">
                        Detener
                      </button>
                    </>
                  ) : null}
                </div>
                {fleetWalk.error ? (
                  <p className="text-xs text-amber-700 dark:text-amber-500">{fleetWalk.error}</p>
                ) : null}
                {fleetCheck ? (
                  <div className="text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                    <p>
                      {fleetCheck.vehicles} vehículos de la cadencia {fleetCheck.regime} con al menos{" "}
                      {MINIMUM_FLEET_DISTANCE_KM} km. Pudo dispararse:{" "}
                      {(["aceleraciones", "frenadas", "giros", "velocidadAlta"] as const)
                        .map((component) => `${COMPONENT_LABELS[component]} en ${fleetCheck.firing[component]}`)
                        .join(" · ")}
                      .
                    </p>
                    <p>
                      Fuera de la cuenta: {fleetCheck.ignored.otherRegime} de otra cadencia,{" "}
                      {fleetCheck.ignored.shortDistance} con menos de {MINIMUM_FLEET_DISTANCE_KM} km
                      {fleetWalk.failures.length ? `, ${fleetWalk.failures.length} sin datos de la telemetría` : ""}.
                    </p>
                    {fleetCheck.ok ? (
                      fleetFresh ? (
                        <p className="text-emerald-700 dark:text-emerald-400">La flota respalda estos umbrales.</p>
                      ) : (
                        <p className="text-amber-700 dark:text-amber-500">
                          Moviste los umbrales o el período después de verificar: vuelve a verificar.
                        </p>
                      )
                    ) : (
                      <Notice title="La flota no respalda estos umbrales">
                        {fleetCheck.reasons.map((reason) => (
                          <span key={reason} className="block">
                            {reason}
                          </span>
                        ))}
                      </Notice>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {scope !== "CADENCIA" && guard && guard.warnings.length ? (
              <div className="mb-3">
                <Notice tone="warning" title="Advertencia estadística del p99">
                  {guard.warnings.map((check) => (
                    <span key={check.family} className="block">
                      {check.explanation}
                    </span>
                  ))}
                  <span className="mt-1 block">
                    Superar el p99 no impide guardar una calibración manual. Revisa la distribución,
                    la proporción y los episodios antes de activar estos valores.
                  </span>
                </Notice>
              </div>
            ) : null}

            {scope !== "CADENCIA" && guard && !guard.ok ? (
              <div className="mb-3">
                <Notice title="Estos umbrales no se pueden guardar">
                  {guard.reasons.map((reason) => (
                    <span key={reason} className="block">
                      {reason}
                    </span>
                  ))}
                  <span className="mt-1 block">
                    El servidor va a rechazar el guardado por estas causas técnicas independientes del p99.
                    Corrige la perilla indicada o carga una muestra apta.
                  </span>
                </Notice>
              </div>
            ) : null}

            <textarea
              className="w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              rows={2}
              maxLength={500}
              placeholder="Nota: con qué criterio se escogieron estos umbrales."
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                // Deshabilitado hasta que se escoja el alcance: es la pregunta de
                // esta tarjeta y no tiene respuesta por omisión.
                disabled={
                  save.isPending ||
                  !scope ||
                  (scope === "CADENCIA" && !fleetReady) ||
                  (scope === "VEHICULO" && guard?.ok === false)
                }
                onClick={() => save.mutate()}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
              >
                {save.isPending
                  ? "Guardando…"
                  : scope === "VEHICULO"
                    ? `Guardar y activar para ${loaded.vehicle.plate ?? loaded.vehicle.deviceId}`
                    : scope === "CADENCIA"
                      ? "Guardar y activar para la cadencia"
                      : "Escoge el alcance"}
              </button>
              {activeForVehicle || activeForRegime ? (
                <button
                  type="button"
                  disabled={revert.isPending}
                  onClick={() => revert.mutate()}
                  className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm dark:border-zinc-700"
                >
                  {loaded.profileSelection.scope === "VEHICULO"
                    ? "Quitarle el perfil propio a este vehículo"
                    : "Volver a los umbrales del código"}
                </button>
              ) : null}
              {save.isSuccess ? (
                <span className="text-xs text-emerald-700 dark:text-emerald-400">
                  Guardado como versión {save.data.version}.{" "}
                  {scope === "VEHICULO"
                    ? `Ya aplica a ${loaded.vehicle.plate ?? loaded.vehicle.deviceId}, y solo a él.`
                    : "Ya aplica a esta cadencia."}
                </span>
              ) : null}
              {save.isSuccess && save.data.verifiedSample ? (
                <span
                  className={`text-xs ${
                    save.data.verifiedSample.matchesLab === false
                      ? "text-amber-700 dark:text-amber-500"
                      : "text-zinc-500 dark:text-zinc-400"
                  }`}
                >
                  {save.data.verifiedSample.matchesLab === false
                    ? `Ojo: la muestra que verificó el servidor (huella ${shortDigest(save.data.verifiedSample.inputDigest)}, ` +
                      `${save.data.verifiedSample.records.toLocaleString("es-CO")} registros) no es la que mostraba el Laboratorio.`
                    : `Verificado sobre la muestra ${shortDigest(save.data.verifiedSample.inputDigest)}.`}
                </span>
              ) : null}
              {save.isSuccess && save.data.statisticalWarnings?.length ? (
                <span className="basis-full text-xs leading-relaxed text-amber-700 dark:text-amber-500">
                  Advertencia del servidor: {save.data.statisticalWarnings.join(" ")}
                </span>
              ) : null}
              {save.isError && !sampleChanged ? (
                <span className="text-xs text-amber-700 dark:text-amber-500">
                  {save.error instanceof ApiError ? save.error.message : "No se pudo guardar."}
                </span>
              ) : null}
            </div>
            {sampleChanged ? (
              <div className="mt-3">
                <Notice tone="warning" title="El servidor no verificó la muestra que estás viendo">
                  <p>
                    {save.isError && save.error instanceof ApiError
                      ? save.error.message
                      : "La calibración se guardó, pero sobre una muestra distinta de la que estás viendo."}
                  </p>
                  <p className="mt-2">
                    Vuelve a cargar la muestra del mismo período: los controles se conservan y las cifras se recalculan
                    sobre lo que devuelve hoy la telemetría.
                  </p>
                  <button
                    type="button"
                    disabled={reloadSample.isPending}
                    onClick={() => reloadSample.mutate()}
                    className="mt-2 rounded-md border border-zinc-300 px-3 py-1.5 text-sm dark:border-zinc-700"
                  >
                    {reloadSample.isPending ? "Volviendo a cargar…" : "Volver a cargar la muestra"}
                  </button>
                  {reloadSample.isError ? (
                    <p className="mt-2">
                      {reloadSample.error instanceof ApiError ? reloadSample.error.message : "No se pudo volver a cargar."}
                    </p>
                  ) : null}
                </Notice>
              </div>
            ) : null}
          </Card>
        </>
      ) : null}

      {profiles.data && profiles.data.profiles.length ? (
        <Card title="Historial de calibraciones" hint="Nada se sobreescribe: cada calibración queda con su versión, su origen y su huella.">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
                  <th className="py-1.5 pr-2 font-medium">Cadencia</th>
                  <th className="py-1.5 pr-2 font-medium">Alcance</th>
                  <th className="py-1.5 pr-2 font-medium">v</th>
                  <th className="py-1.5 pr-2 text-right font-medium">Acel.</th>
                  <th className="py-1.5 pr-2 text-right font-medium">Frenada</th>
                  <th className="py-1.5 pr-2 text-right font-medium">Giro</th>
                  <th className="py-1.5 pr-2 font-medium">Calibrado con</th>
                  <th className="py-1.5 pr-2 font-medium">Por</th>
                  <th className="py-1.5 pr-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {profiles.data.profiles.map((profile) => (
                  <tr
                    key={`${profile.regime}-${profile.version}`}
                    className="border-b border-zinc-100 last:border-0 dark:border-zinc-800/60"
                  >
                    <td className="py-1.5 pr-2 text-xs">{REGIME_LABEL[profile.regime] ?? profile.regime}</td>
                    {/* Alcance, no procedencia: con qué vehículo se midió está en
                        «Calibrado con». Son dos preguntas distintas. */}
                    <td className="py-1.5 pr-2 text-xs">
                      {profile.scopeDevice ? (
                        <span className="rounded bg-zinc-100 px-1.5 py-0.5 dark:bg-zinc-800">
                          solo {profile.calibratedFromPlate ?? profile.scopeDevice}
                        </span>
                      ) : (
                        <span className="text-zinc-500">toda la cadencia</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 tabular-nums text-zinc-500">{profile.version}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">
                      {num(profile.thresholds.accelerationThresholdMps2, 3)}
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">
                      {num(profile.thresholds.brakingThresholdMps2, 3)}
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">
                      {num(profile.thresholds.minimumYawRateDegreesPerSecond, 3)}
                    </td>
                    <td className="py-1.5 pr-2 text-xs text-zinc-500">
                      {profile.calibratedFromPlate ?? "—"}
                      {profile.sampleSegments ? ` · ${profile.sampleSegments} tramos` : ""}
                    </td>
                    <td className="py-1.5 pr-2 text-xs text-zinc-500">{profile.calibratedBy}</td>
                    <td className="py-1.5 pr-2 text-xs">
                      {profile.active ? (
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200">
                          Activo
                        </span>
                      ) : (
                        <span className="text-zinc-400">histórico</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Los umbrales del manual oficial, traducidos a lo que el motor sabe mover hoy.
 *
 * Dos traducciones que NO son literales, y por eso están dichas aquí:
 *
 *  - El giro del manual es una aceleración lateral de 3,4 m/s², que depende de
 *    la velocidad. El motor usa un mínimo fijo de °/s, así que se calcula el
 *    equivalente a 40 km/h —la velocidad mínima que el manual puntúa— que es el
 *    punto más exigente del rango. Por encima de esa velocidad, el manual
 *    detectaría giros que este umbral deja pasar.
 *  - El manual pide velocidad mínima de 10 km/h en ambos extremos para contar
 *    aceleración o frenada. El motor filtra por cambio mínimo de velocidad, que
 *    no es lo mismo; se deja en 0 para no sumar un filtro que el manual no pide.
 *
 * Las dos diferencias son cambios del detector, no de un número, y quedan
 * pendientes. Mientras tanto esto acerca el motor al manual todo lo que se
 * puede sin tocar el detector.
 */
function manualPreset(current: TunableThresholds): TunableThresholds {
  const yaw = manualYawRateAt(MANUAL_OFICIAL_V3.minimumTurnSpeedKmh);
  return {
    ...current,
    accelerationThresholdMps2: MANUAL_OFICIAL_V3.accelerationThresholdMps2,
    brakingThresholdMps2: MANUAL_OFICIAL_V3.brakingThresholdMps2,
    minimumSpeedChangeKmh: MANUAL_OFICIAL_V3.minimumSpeedChangeKmh,
    minimumYawRateDegreesPerSecond: yaw,
    minimumHeadingChangeDegrees: MANUAL_OFICIAL_V3.minimumHeadingChangeDegrees,
    minimumReferenceSpeedKmh: MANUAL_OFICIAL_V3.minimumTurnSpeedKmh,
    // El criterio de verdad del manual. Con esto puesto, la velocidad angular
    // de arriba deja de mandar y pasa a ser solo informativa.
    lateralAccelerationMps2: MANUAL_OFICIAL_V3.lateralAccelerationMps2,
    operationalSpeedLimitKmh: MANUAL_OFICIAL_V3.operationalSpeedLimitKmh,
    // Histéresis del manual: abre en 80 y no cierra hasta bajar de 76. Sin
    // esto, oscilar alrededor del límite abre un episodio en cada cruce —839
    // tramos en 3.104 km de autopista a 80–86 km/h, medido en la flota.
    operationalSpeedExitKmh: MANUAL_OFICIAL_V3.operationalSpeedExitKmh,
    // 5 s de velocidad sostenida, sumando tramos. Sin esto, un equipo que
    // reporte más seguido que cada 5 s no puede medir velocidad elevada.
    sustainedWindowSeconds: 5,
    // Bandas de severidad proporcionales al umbral, para que un episodio
    // detectado siempre tenga severidad que asignarle.
    severityAcceleration: [2.5, 3.5, 4.5],
    severityBraking: [2.5, 3.5, 4.5],
    severityYawRate: [yaw, yaw * 1.5, yaw * 2],
  };
}

/**
 * Arma el perfil que se le pasa al motor con los umbrales de la pantalla.
 *
 * Parte del perfil del código del MISMO régimen, así que todo lo que NO es
 * perilla —ventanas de encadenamiento, reglas de calidad, corte de viajes—
 * queda exactamente como en producción. Solo se sustituye lo que la pantalla
 * deja mover; si la mesa de trabajo cambiara algo más, estaría calibrando un
 * motor distinto del que genera los informes.
 */
function profileFromThresholds(
  loaded: CalibrationVehicleResponse,
  thresholds: TunableThresholds,
  profileId: string,
  regime: "HIGH" | "MEDIUM" | "LOW",
): AnalysisProfile {
  const base = PROFILE_FAMILY.find((profile) => profile.regime === regime) ?? PROFILE_FAMILY[0];
  return {
    // MISMA función que usa el servidor para armar el perfil del informe. Que
    // sea la misma es el punto: si aquí se aplicara una regla distinta, la
    // mesa de trabajo estaría calibrando un motor que no es el que publica.
    ...applyThresholds(base, thresholds),
    profileId,
    calibration: loaded.thresholdSource === "CALIBRADO" ? "CALIBRATED" : "UNCALIBRATED",
  };
}
